import React from 'react';

export type Pagination<T> = {
  /** The rows to render — the first `page × pageSize` of `items`. */
  visible: T[];
  /** Is there another page to reveal? Drives the "Load more" button. */
  hasMore: boolean;
  /** Advance one page. */
  loadMore: () => void;
};

/**
 * Client-side "load more" pagination over a fully-loaded list.
 *
 * While these screens run on mock data the whole list is already in memory, so
 * we just reveal it `pageSize` rows at a time. The returned shape mirrors what a
 * paged endpoint gives back — `visible` (rows to render), `hasMore` (is there
 * another page) and `loadMore()` (advance one page) — so when the API lands the
 * call sites don't change: swap this hook for one that fetches the next page and
 * appends, driving `hasMore` off the server response.
 *
 * Pass `resetKey` (e.g. the active tab/filter) to snap back to the first page
 * whenever the underlying view changes, so "load more" never carries a stale
 * offset across views. The reset happens during render (no flash of extra rows).
 */
export const usePagination = <T>(items: T[], pageSize: number, resetKey?: unknown): Pagination<T> => {
  const [page, setPage] = React.useState(1);
  const [key, setKey] = React.useState(resetKey);

  // Adjust state during render when the view changes — see React docs on
  // "storing information from previous renders". Resets synchronously, so we
  // never paint a stale (too-large) slice for a frame.
  if (key !== resetKey) {
    setKey(resetKey);
    setPage(1);
  }

  const shown = page * pageSize;
  const loadMore = React.useCallback(() => setPage((p) => p + 1), []);

  return { visible: items.slice(0, shown), hasMore: shown < items.length, loadMore };
};

export default usePagination;
